import type { LinkType } from '../link';
import * as React from 'react';
import { Manager, Reference, Popper } from 'react-popper';
import { rem } from 'polished';

import Link from './Link';
import { styled } from './themeContext';
import DevsistersGames from './gameNavigation/DevsistersGames';
import _MobileNavigationBar from './gameNavigation/MobileNavigationBar';
import GameLanguageSelector from './gameNavigation/GameLanguageSelector';
import { ReactComponent as CloseIconSvg } from './gameNavigation/close.svg';
import { ReactComponent as _DevsistersGamesIcon } from './gameNavigation/devsisters-games.svg';
import { carouselContext, useInitNavigation } from '../carouselContext';

type GameNavigationProps = {
  csLabel: string,
  csLink: LinkType,
  currentLocale: string,
  otherGames: readonly GatsbyTypes.DevsistersGamesGameItemFragment[],
  upcomingLabel: string;
  preLabel: string;
}

const Container = styled.div((props) => ({
  height: rem(40),
  backgroundColor: 'rgb(25, 29, 36)',
  display: 'none',
  [props.theme.media['lg']]: {
    display: 'block',
  },
}));

const ContentBox = styled.div({
  position: 'relative',
  padding: `0 1em`,
  height: '100%',
  fontSize: rem(16),
});

const Contents = styled.div({
  maxWidth: rem(1250),
  height: '100%',
  margin: `0 auto`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  position: 'relative',
})

const TextButton = styled.button({
  padding: 0,
  color: 'rgb(174, 174, 179)',
  fontSize: '1em',
  fontFamily: 'inherit',
  textDecoration: 'none',
  border: 'none',
  outline: 'none',
  cursor: 'pointer',
});

const TextLink = TextButton.withComponent(Link);
const TextLangSelector = TextButton.withComponent(GameLanguageSelector);

const DevsistersWrapper = styled.div<{ active: boolean }>((props) => ({
  width: rem(434),
  zIndex: 2,
  '& > div': {
    willChange: 'transform',
    transform: props.active ? 'scaleY(1)' : 'scaleY(0)',
    transition: 'transform .1s ease-in-out',
  },
}));

const DevsistersGamesButton = styled(TextButton)((props) => ({
  border: 'none',
  outline: 'none',
  cursor: 'pointer',
  padding: 0,
  background: 'transparent',
  color: '#fff',
  fontSize: rem(16),
  right: 0,
  [props.theme.media['lg']]: {
    background: 'rgb(25, 29, 36)',
    color: 'rgb(174, 174, 179)',
  },
}));

const DevsistersGamesIcon = styled(_DevsistersGamesIcon)((props) => ({
  color: '#fff',
  width: rem(140),
  height: 'auto',
  marginTop: rem(4),
  '> *': {
    fill: 'currentColor',
  },
  [props.theme.media['sm']]: {
    width: rem(200),
  },
  [props.theme.media['md']]: {
    width: rem(222),
  },
  [props.theme.media['lg']]: {
    color: 'rgb(174, 174, 179)',
    width: rem(152),
    height: rem(16),
    marginTop: rem(4),
    '> *': {
      fill: 'currentColor',
    },
  },
}));

const DevsistersGamesCloseButton = styled('button')({
  position: 'absolute',
  width: rem(65),
  height: rem(65),
  outline: 'none',
  border: 'none',
  padding: 0,
  background: 'rgba(25, 29, 36, 0.95)',
  top: 0,
  left: rem(480),
  lineHeight: 0,
  cursor: 'pointer',
});

const DevsistersGamesCloseIcon = styled(CloseIconSvg)({
  color: '#fff',
  fontSize: rem(30),
});

const RightContainer = styled.div({
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: rem(14),
  '> *': {
    marginRight: '3.5em',
  },
  '> :last-child': {
    marginRight: 0,
  },
});

const CommonNavigation: React.FC<GameNavigationProps> = ({
  csLabel,
  csLink,
  currentLocale,
  otherGames,
  upcomingLabel,
  preLabel,
}) => {
  const { setPlayCarousel } = React.useContext(carouselContext);
  const hasOtherGames = otherGames.length >= 1;
  const [popoverOpened, setPopoverOpened] = React.useState(false);
  const togglePopover = () => {
    setPopoverOpened((prevPopoverOpened) => !prevPopoverOpened);
    setPlayCarousel((prevPlayCarousel) => !prevPlayCarousel);
  };
  useInitNavigation(popoverOpened, setPopoverOpened);
  return (
    <>
      <Container>
        <div
          css={{
            maxWidth: rem(1250),
            position: 'relative',
            height: '100%',
            margin: '0 auto',
          }}
        >
          <Manager>
            <Reference>
              {({ ref }) => (
                <ContentBox ref={ref}>
                  <Contents>
                    {hasOtherGames ? (
                      <DevsistersGamesButton
                        onClick={togglePopover}
                        data-testid="devsisters-games-button"
                      >
                        <DevsistersGamesIcon />
                      </DevsistersGamesButton>
                    ) : (
                      <div />
                    )}
                    <RightContainer>
                      <TextLink link={csLink}>{csLabel}</TextLink>
                      <TextLangSelector currentLocale={currentLocale} />
                    </RightContainer>
                  </Contents>
                </ContentBox>
              )}
            </Reference>
            {hasOtherGames && (
              <Popper
                eventsEnabled
                placement="bottom-start"
              >
                {({ ref, style, placement }) => (
                  <DevsistersWrapper
                    ref={ref}
                    style={style}
                    active={popoverOpened}
                    data-placement={placement}
                  >
                    <div>
                      <DevsistersGames onClickItem={togglePopover} otherGames={otherGames} upcomingLabel={upcomingLabel} preLabel={preLabel} />
                      <DevsistersGamesCloseButton onClick={togglePopover}>
                        <DevsistersGamesCloseIcon />
                      </DevsistersGamesCloseButton>
                    </div>
                  </DevsistersWrapper>
                )}
              </Popper>
            )}
          </Manager>
        </div>
      </Container>
    </>
  );
};

export default CommonNavigation;
